import React from 'react';
import { SxProps, Theme } from '@mui/material';

import { PropertySelectField } from '@paragon/client-tools';
import { AddLoadTo, ComponentDesign, LoadCase, LoadType } from '@paragon/contract-library';
import { getLoadTypeOption, Option } from '@paragon/layout-models';

interface Props {
  loadCases: LoadCase[];
  componentDesign: ComponentDesign;
  loadType: LoadType;
  loadCaseIdentification: AddLoadTo;
  specifiedLoadCaseName: string | null;
  onSelectLoadDestination: (loadCaseIdentification: AddLoadTo, specifiedLoadCaseName: string | null) => void;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

export function AddManualLoadToSelect(props: Props) {
  const options: { [key: string]: Option<string> } = {
    [AddLoadTo.LoadCasesWithSpecifiedLoadType]: {
      name: `All Load Cases With Selected Type (${getLoadTypeOption(props.loadType).name})`,
      value: AddLoadTo.LoadCasesWithSpecifiedLoadType as string,
    },
    [AddLoadTo.StationaryLoadCasesWithSpecifiedLoadType]: {
      name: `All Load Cases With Selected Type (${getLoadTypeOption(props.loadType).name}) Except Moving Load Cases`,
      value: AddLoadTo.StationaryLoadCasesWithSpecifiedLoadType as string,
    },
  };
  const loadCasesFilteredToMatchLoadType = props.loadCases.filter(
    (loadCase) =>
      loadCase.loads.some((load) => load.loadType === props.loadType) ||
      props.componentDesign.additionalLoads?.manualLoadCases.some(
        (manualLoadCase) => manualLoadCase.guid === loadCase.guid,
      ),
  );
  for (const loadCase of loadCasesFilteredToMatchLoadType) {
    options[loadCase.name] = {
      name: loadCase.name,
      value: loadCase.name,
    };
  }

  return (
    <PropertySelectField
      sx={props.sx}
      options={Object.values(options)}
      getName={(option) => option.name}
      getValue={(option) => option.value}
      value={
        props.loadCaseIdentification === AddLoadTo.SpecificLoadCase && props.specifiedLoadCaseName
          ? (options[props.specifiedLoadCaseName] ?? {
              name: props.specifiedLoadCaseName,
              value: props.specifiedLoadCaseName,
            })
          : options[props.loadCaseIdentification]
      }
      submit={(value) => {
        const { loadCaseIdentification, specifiedLoadCaseName } = getLoadCaseDestinationAndSpecifiedLoadCaseName(
          value.value,
        );
        props.onSelectLoadDestination(loadCaseIdentification, specifiedLoadCaseName);
      }}
      property="Add Load To"
      disabled={props.disabled}
      allowUnknownValues // For the case when load cases have been invalidated but a manual load is assigned to a specific load case
    />
  );
}

function getLoadCaseDestinationAndSpecifiedLoadCaseName(value: string | null) {
  let loadCaseIdentification;
  let specifiedLoadCaseName;
  if (
    value === AddLoadTo.LoadCasesWithSpecifiedLoadType ||
    value === AddLoadTo.StationaryLoadCasesWithSpecifiedLoadType
  ) {
    specifiedLoadCaseName = null;
    loadCaseIdentification = value;
  } else {
    specifiedLoadCaseName = value;
    loadCaseIdentification = AddLoadTo.SpecificLoadCase;
  }
  return { loadCaseIdentification, specifiedLoadCaseName };
}
